import {
  Col,
  Form,
  Row,
  Select,
  Spin,
  Table,
  Typography,
  Popover,
  DatePicker,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { Component } from "react";
import { PmsAPI } from "../../../controller/PmsAPI";
import DateWithWeekday from "../../components/DateWithWeekday";
import PmsRoomSelect from "../../components/PmsRoomSelect";
import CSVDownloadButton from "../../components/CSVDownloadButton";

export class RevParComparisonPage extends Component {
  state = {
    loading: false,
    periodType: "month",
    revparData: [],
    roomTypes: [],
    selectedRoomTypes: [], // 選中的房型列表，空陣列表示全選
    dateRange: null, // 日期範圍篩選（用於日週期）
    yearRange: null, // 年份範圍篩選（用於年週期）
    monthRange: null, // 月份範圍篩選（用於月週期）
  };

  componentDidMount() {
    this.refresh();
  }

  refresh = () => {
    this.fetchRevParData();
  };

  fetchRevParData = async () => {
    this.setState({ loading: true });
    
    try {
      // Get all room types from the room index API
      const roomResponse = await PmsAPI.room.index();
      const allRoomTypes = roomResponse.data.items || [];
      
      // Define common room types based on the existing code
      const commonRoomTypes = [
        { room_cod: 'CEO', room_nam: 'CEO' },
        { room_cod: 'CON', room_nam: 'CON' },
        { room_cod: 'FMB', room_nam: 'FMB' },
        { room_cod: 'NFM', room_nam: 'NFM' },
        { room_cod: 'FM', room_nam: 'FM' },
        { room_cod: 'S4', room_nam: 'S4' },
        { room_cod: 'DTB', room_nam: 'DTB' },
        { room_cod: 'DTL', room_nam: 'DTL' },
        { room_cod: 'DT', room_nam: 'DT' },
        { room_cod: 'ACC', room_nam: 'ACC' },
        { room_cod: 'STB', room_nam: 'STB' },
        { room_cod: 'STL', room_nam: 'STL' }
      ];
      
      // Use room types from API if available, otherwise use common types
      const roomTypesToUse = allRoomTypes.length > 0 ? allRoomTypes : commonRoomTypes;
      
      // Get RevPar data for each room type
      const revparPromises = roomTypesToUse.map(async (roomType) => {
        try {
          const response = await PmsAPI.houserate.stat(this.state.periodType, roomType.room_cod);
          return {
            roomType: roomType.room_cod,
            roomName: roomType.room_nam || roomType.room_cod,
            data: response.data.items || []
          };
        } catch (error) {
          console.error(`Error fetching data for room type ${roomType.room_cod}:`, error);
          return {
            roomType: roomType.room_cod,
            roomName: roomType.room_nam || roomType.room_cod,
            data: []
          };
        }
      });

      // Also get overall data (no room filter)
      const overallPromise = PmsAPI.houserate.stat(this.state.periodType, null)
        .then(response => ({
          roomType: 'ALL',
          roomName: '全部',
          data: response.data.items || []
        }))
        .catch(error => {
          console.error('Error fetching overall data:', error);
          return {
            roomType: 'ALL',
            roomName: '全部',
            data: []
          };
        });

      const [overallData, ...roomTypeData] = await Promise.all([overallPromise, ...revparPromises]);
      
      // Combine all data
      const allData = [overallData, ...roomTypeData.filter(item => item.data.length > 0)];
      
      this.setState({
        revparData: allData,
        roomTypes: roomTypesToUse
      });
    } catch (error) {
      console.error('Error fetching RevPar data:', error);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSelectPeriodType = (value) => {
    console.log("handleSelectPeriodType", value);
    // 切換週期時清除之前的篩選條件
    this.setState({
      periodType: value,
      dateRange: null,
      yearRange: null,
      monthRange: null
    }, () => this.refresh());
  };

  handleRoomTypeChange = (selectedValues) => {
    console.log("handleRoomTypeChange", selectedValues);
    this.setState({ selectedRoomTypes: selectedValues || [] });
  };

  handleDateRangeChange = (dates) => {
    console.log("handleDateRangeChange", dates);
    this.setState({ dateRange: dates });
  };

  handleYearRangeChange = (dates) => {
    console.log("handleYearRangeChange", dates);
    this.setState({ yearRange: dates });
  };

  handleMonthRangeChange = (dates) => {
    console.log("handleMonthRangeChange", dates);
    this.setState({ monthRange: dates });
  };

  // Create table data by combining all room types' data by date
  getTableData = () => {
    const { revparData, dateRange, yearRange, monthRange, periodType } = this.state;
    if (!revparData.length) return [];

    // Get all unique dates from all room types
    const allDates = new Set();
    revparData.forEach(roomData => {
      roomData.data.forEach(item => {
        allDates.add(item.dat);
      });
    });

    // Convert to array and sort (newest first)
    let sortedDates = Array.from(allDates).sort((a, b) => new Date(b) - new Date(a));

    // Apply different filters based on period type
    if (periodType === 'day' && dateRange && dateRange.length === 2) {
      // 日週期：使用日期範圍篩選
      const [startDate, endDate] = dateRange;
      sortedDates = sortedDates.filter(date => {
        const currentDate = new Date(date);
        return currentDate >= startDate.startOf('day').toDate() &&
               currentDate <= endDate.endOf('day').toDate();
      });
    } else if (periodType === 'month' && monthRange && monthRange.length === 2) {
      // 月週期：使用月份範圍篩選
      const [startMonth, endMonth] = monthRange;
      sortedDates = sortedDates.filter(date => {
        const currentDate = new Date(date);
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        
        const startYear = startMonth.year();
        const startMonthNum = startMonth.month();
        const endYear = endMonth.year();
        const endMonthNum = endMonth.month();
        
        // 比較年月
        const currentYearMonth = currentYear * 12 + currentMonth;
        const startYearMonth = startYear * 12 + startMonthNum;
        const endYearMonth = endYear * 12 + endMonthNum;
        
        return currentYearMonth >= startYearMonth && currentYearMonth <= endYearMonth;
      });
    } else if (periodType === 'year' && yearRange && yearRange.length === 2) {
      // 年週期：使用年份範圍篩選
      const [startYear, endYear] = yearRange;
      const startYearNum = startYear.year();
      const endYearNum = endYear.year();
      
      sortedDates = sortedDates.filter(date => {
        const currentDate = new Date(date);
        const currentYear = currentDate.getFullYear();
        return currentYear >= startYearNum && currentYear <= endYearNum;
      });
    }

    // Create table rows
    return sortedDates.map(date => {
      const row = {
        key: date,
        date: date,
      };

      // Add all data for each room type
      revparData.forEach(roomData => {
        const dateData = roomData.data.find(item => item.dat === date);
        if (dateData) {
          row[`revpar_${roomData.roomType}`] = dateData.revpar;
          row[`adr_${roomData.roomType}`] = dateData.adr;
          row[`occupancy_${roomData.roomType}`] = dateData.occupancy_rate;
          row[`use_qnt_${roomData.roomType}`] = dateData.use_qnt;
          row[`total_rooms_${roomData.roomType}`] = dateData.total_rooms;
          row[`rent_amt_${roomData.roomType}`] = dateData.rent_amt;
          row[`rs_amt_${roomData.roomType}`] = dateData.rs_amt;
          row[`total_revenue_${roomData.roomType}`] = dateData.total_revenue;
          row[`rent_avg_${roomData.roomType}`] = dateData.rent_amt && dateData.use_qnt ? Math.round(dateData.rent_amt / dateData.use_qnt) : null;
        } else {
          row[`revpar_${roomData.roomType}`] = null;
          row[`adr_${roomData.roomType}`] = null;
          row[`occupancy_${roomData.roomType}`] = null;
          row[`use_qnt_${roomData.roomType}`] = null;
          row[`total_rooms_${roomData.roomType}`] = null;
          row[`rent_amt_${roomData.roomType}`] = null;
          row[`rs_amt_${roomData.roomType}`] = null;
          row[`total_revenue_${roomData.roomType}`] = null;
          row[`rent_avg_${roomData.roomType}`] = null;
        }
      });

      return row;
    });
  };

  // Create dynamic columns based on available room types and filter
  getColumns = () => {
    const { revparData, selectedRoomTypes } = this.state;
    
    // Filter room types based on selection
    const filteredRevparData = selectedRoomTypes.length === 0
      ? revparData // 空陣列表示全選
      : revparData.filter(roomData =>
          roomData.roomType === 'ALL' || selectedRoomTypes.includes(roomData.roomType)
        );
    
    const baseColumns = [
      {
        title: "日期",
        dataIndex: "date",
        key: "date",
        fixed: "left",
        width: 100,
        sorter: (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        defaultSortOrder: 'ascend',
        render: (date) => <DateWithWeekday date={date} />,
      },
    ];

    // Get filtered dates for sorting calculation
    const { dateRange, yearRange, monthRange, periodType } = this.state;
    let filteredDates = [];
    
    if (revparData.length > 0) {
      // Get all unique dates from all room types
      const allDates = new Set();
      revparData.forEach(roomData => {
        roomData.data.forEach(item => {
          allDates.add(item.dat);
        });
      });
      
      // Convert to array and apply filters
      filteredDates = Array.from(allDates);
      
      // Apply different filters based on period type
      if (periodType === 'day' && dateRange && dateRange.length === 2) {
        const [startDate, endDate] = dateRange;
        filteredDates = filteredDates.filter(date => {
          const currentDate = new Date(date);
          return currentDate >= startDate.startOf('day').toDate() &&
                 currentDate <= endDate.endOf('day').toDate();
        });
      } else if (periodType === 'month' && monthRange && monthRange.length === 2) {
        const [startMonth, endMonth] = monthRange;
        filteredDates = filteredDates.filter(date => {
          const currentDate = new Date(date);
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth();
          
          const startYear = startMonth.year();
          const startMonthNum = startMonth.month();
          const endYear = endMonth.year();
          const endMonthNum = endMonth.month();
          
          const currentYearMonth = currentYear * 12 + currentMonth;
          const startYearMonth = startYear * 12 + startMonthNum;
          const endYearMonth = endYear * 12 + endMonthNum;
          
          return currentYearMonth >= startYearMonth && currentYearMonth <= endYearMonth;
        });
      } else if (periodType === 'year' && yearRange && yearRange.length === 2) {
        const [startYear, endYear] = yearRange;
        const startYearNum = startYear.year();
        const endYearNum = endYear.year();
        
        filteredDates = filteredDates.filter(date => {
          const currentDate = new Date(date);
          const currentYear = currentDate.getFullYear();
          return currentYear >= startYearNum && currentYear <= endYearNum;
        });
      }
    }

    // Sort room types by average RevPAR (descending), but keep 'ALL' at the front
    const sortedRevparData = [...filteredRevparData].sort((a, b) => {
      // 'ALL' 永遠放在最前面
      if (a.roomType === 'ALL') return -1;
      if (b.roomType === 'ALL') return 1;
      
      // 其他房型按平均 RevPAR 排序，只計算篩選後日期範圍內的數據
      const filteredDataA = a.data.filter(item => filteredDates.includes(item.dat));
      const filteredDataB = b.data.filter(item => filteredDates.includes(item.dat));
      
      const avgA = filteredDataA.length > 0 ?
        filteredDataA.reduce((sum, item) => sum + (item.revpar || 0), 0) / filteredDataA.length : 0;
      const avgB = filteredDataB.length > 0 ?
        filteredDataB.reduce((sum, item) => sum + (item.revpar || 0), 0) / filteredDataB.length : 0;
      return avgB - avgA; // 降序排列，RevPAR 高的在前
    });

    // Add grouped columns for each room type - ADR and RevPAR under each room type
    const roomTypeColumns = sortedRevparData.map(roomData => ({
      title: roomData.roomName,
      key: `group_${roomData.roomType}`,
      children: [
        {
          title: 'ADR',
          dataIndex: `adr_${roomData.roomType}`,
          key: `adr_${roomData.roomType}`,
          align: "right",
          width: 80,
          render: (value, record) => {
            if (value === null || value === undefined) return "-";
            
            const numValue = Number(value);
            const revparValue = record[`revpar_${roomData.roomType}`];
            const occupancyValue = record[`occupancy_${roomData.roomType}`];
            const useQntValue = record[`use_qnt_${roomData.roomType}`];
            const totalRoomsValue = record[`total_rooms_${roomData.roomType}`];
            const rentAmtValue = record[`rent_amt_${roomData.roomType}`];
            const rsAmtValue = record[`rs_amt_${roomData.roomType}`];
            const totalRevenueValue = record[`total_revenue_${roomData.roomType}`];
            const rentAvgValue = record[`rent_avg_${roomData.roomType}`];
            
            // Popover content with all detailed information
            const popoverContent = (
              <div style={{ minWidth: 250 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>{roomData.roomName}</strong>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <DateWithWeekday date={record.date} />
                </div>
                <hr style={{ margin: '8px 0' }} />
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '13px' }}>
                  <div><strong>ADR：</strong></div>
                  <div style={{ textAlign: 'right' }}>NT$ {Math.round(numValue).toLocaleString()}</div>
                  
                  <div><strong>RevPAR：</strong></div>
                  <div style={{ textAlign: 'right' }}>{revparValue ? `NT$ ${Math.round(Number(revparValue)).toLocaleString()}` : '-'}</div>
                  
                  <div><strong>住房率：</strong></div>
                  <div style={{ textAlign: 'right' }}>{occupancyValue ? `${Number(occupancyValue).toFixed(2)}%` : '-'}</div>
                  
                  <div><strong>住房間數：</strong></div>
                  <div style={{ textAlign: 'right' }}>{useQntValue ? Math.round(Number(useQntValue)).toLocaleString() : '-'}</div>
                  
                  <div><strong>總房間數：</strong></div>
                  <div style={{ textAlign: 'right' }}>{totalRoomsValue ? Math.round(Number(totalRoomsValue)).toLocaleString() : '-'}</div>
                  
                  <div><strong>房租收入：</strong></div>
                  <div style={{ textAlign: 'right' }}>{rentAmtValue ? `NT$ ${Math.round(Number(rentAmtValue)).toLocaleString()}` : '-'}</div>
                  
                  <div><strong>餐飲收入：</strong></div>
                  <div style={{ textAlign: 'right' }}>{rsAmtValue ? `NT$ ${Math.round(Number(rsAmtValue)).toLocaleString()}` : '-'}</div>
                  
                  <div><strong>總收入：</strong></div>
                  <div style={{ textAlign: 'right' }}>{totalRevenueValue ? `NT$ ${Math.round(Number(totalRevenueValue)).toLocaleString()}` : '-'}</div>
                  
                  <div><strong>平均房價：</strong></div>
                  <div style={{ textAlign: 'right' }}>{rentAvgValue ? `NT$ ${Math.round(Number(rentAvgValue)).toLocaleString()}` : '-'}</div>
                </div>
                
                <hr style={{ margin: '8px 0' }} />
                <div style={{ fontSize: '11px', color: '#666', textAlign: 'center' }}>
                  RevPAR = ADR × 住房率
                </div>
              </div>
            );
            
            return (
              <Popover
                content={popoverContent}
                title="詳細資訊"
                trigger="hover"
                placement="top"
              >
                <span style={{ cursor: 'pointer', color: '#1890ff' }}>
                  {Math.round(numValue).toLocaleString()}
                </span>
              </Popover>
            );
          },
          sorter: (a, b) => {
            const aVal = a[`adr_${roomData.roomType}`] || 0;
            const bVal = b[`adr_${roomData.roomType}`] || 0;
            return aVal - bVal;
          },
        },
        {
          title: 'RevPAR',
          dataIndex: `revpar_${roomData.roomType}`,
          key: `revpar_${roomData.roomType}`,
          align: "right",
          width: 80,
          render: (value, record) => {
            if (value === null || value === undefined) return "-";
            
            const numValue = Number(value);
            const adrValue = record[`adr_${roomData.roomType}`];
            const occupancyValue = record[`occupancy_${roomData.roomType}`];
            const useQntValue = record[`use_qnt_${roomData.roomType}`];
            const totalRoomsValue = record[`total_rooms_${roomData.roomType}`];
            const rentAmtValue = record[`rent_amt_${roomData.roomType}`];
            const rsAmtValue = record[`rs_amt_${roomData.roomType}`];
            const totalRevenueValue = record[`total_revenue_${roomData.roomType}`];
            const rentAvgValue = record[`rent_avg_${roomData.roomType}`];
            
            let color = '';
            if (numValue < 1000) {
              color = '#cf1322'; // 紅色 - 表現較差
            } else if (numValue >= 1000 && numValue <= 2500) {
              color = '#faad14'; // 黃色 - 表現中等
            } else if (numValue > 2500) {
              color = '#3f8600'; // 綠色 - 表現優秀
            }
            
            // Popover content with all detailed information
            const popoverContent = (
              <div style={{ minWidth: 250 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>{roomData.roomName}</strong>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <DateWithWeekday date={record.date} />
                </div>
                <hr style={{ margin: '8px 0' }} />
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '13px' }}>
                  <div><strong>RevPAR：</strong></div>
                  <div style={{ textAlign: 'right', color }}>NT$ {Math.round(numValue).toLocaleString()}</div>
                  
                  <div><strong>ADR：</strong></div>
                  <div style={{ textAlign: 'right' }}>{adrValue ? `NT$ ${Math.round(Number(adrValue)).toLocaleString()}` : '-'}</div>
                  
                  <div><strong>住房率：</strong></div>
                  <div style={{ textAlign: 'right' }}>{occupancyValue ? `${Number(occupancyValue).toFixed(2)}%` : '-'}</div>
                  
                  <div><strong>住房間數：</strong></div>
                  <div style={{ textAlign: 'right' }}>{useQntValue ? Math.round(Number(useQntValue)).toLocaleString() : '-'}</div>
                  
                  <div><strong>總房間數：</strong></div>
                  <div style={{ textAlign: 'right' }}>{totalRoomsValue ? Math.round(Number(totalRoomsValue)).toLocaleString() : '-'}</div>
                  
                  <div><strong>房租收入：</strong></div>
                  <div style={{ textAlign: 'right' }}>{rentAmtValue ? `NT$ ${Math.round(Number(rentAmtValue)).toLocaleString()}` : '-'}</div>
                  
                  <div><strong>餐飲收入：</strong></div>
                  <div style={{ textAlign: 'right' }}>{rsAmtValue ? `NT$ ${Math.round(Number(rsAmtValue)).toLocaleString()}` : '-'}</div>
                  
                  <div><strong>總收入：</strong></div>
                  <div style={{ textAlign: 'right' }}>{totalRevenueValue ? `NT$ ${Math.round(Number(totalRevenueValue)).toLocaleString()}` : '-'}</div>
                  
                  <div><strong>平均房價：</strong></div>
                  <div style={{ textAlign: 'right' }}>{rentAvgValue ? `NT$ ${Math.round(Number(rentAvgValue)).toLocaleString()}` : '-'}</div>
                </div>
                
                <hr style={{ margin: '8px 0' }} />
                <div style={{ fontSize: '11px', color: '#666', textAlign: 'center' }}>
                  RevPAR = ADR × 住房率
                </div>
              </div>
            );
            
            return (
              <Popover
                content={popoverContent}
                title="詳細資訊"
                trigger="hover"
                placement="top"
              >
                <span style={{ color, cursor: 'pointer' }}>
                  {Math.round(numValue).toLocaleString()}
                </span>
              </Popover>
            );
          },
          sorter: (a, b) => {
            const aVal = a[`revpar_${roomData.roomType}`] || 0;
            const bVal = b[`revpar_${roomData.roomType}`] || 0;
            return aVal - bVal;
          },
        }
      ]
    }));

    return [...baseColumns, ...roomTypeColumns];
  };

  render() {
    const tableData = this.getTableData();
    const columns = this.getColumns();

    return (
      <Spin spinning={this.state.loading}>
        <h1>ADR & RevPAR 房型比較</h1>
        <div style={{ marginBottom: 16 }}>
          <Typography.Title level={5}>指標說明</Typography.Title>
          <div style={{ padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Typography.Text strong>ADR (Average Daily Rate)</Typography.Text>
              <Typography.Text type="secondary"> - 平均房價</Typography.Text>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: '12px' }}>
              計算方式：房租收入 ÷ 已售房間數，代表每間已售房間的平均收入
            </Typography.Text>
            
            <div style={{ marginBottom: '8px' }}>
              <Typography.Text strong>RevPAR (Revenue Per Available Room)</Typography.Text>
              <Typography.Text type="secondary"> - 每間可售房收入</Typography.Text>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: '12px' }}>
              計算方式：ADR × 住房率 或 房租收入 ÷ 總房間數，RevPAR 同時考慮了房價和住房率，比單純的 ADR 更能反映飯店的整體營收表現。
              追蹤 RevPAR 趨勢有助於優化定價策略和提升整體收益。
            </Typography.Text>
            
            <div style={{ marginBottom: '12px' }}>
              <Typography.Text strong style={{ color: '#1890ff' }}>RevPAR 數字判讀指南：</Typography.Text>
              <div style={{ marginTop: '8px', paddingLeft: '12px' }}>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#cf1322', fontWeight: 'bold' }}>• 低於 1,000</span>
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}> - 表現較差，需要檢討定價策略或提升住房率</Typography.Text>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#faad14', fontWeight: 'bold' }}>• 1,000 - 2,500</span>
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}> - 表現中等，有改善空間</Typography.Text>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#3f8600', fontWeight: 'bold' }}>• 高於 2,500</span>
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}> - 表現優秀，收益管理效果良好</Typography.Text>
                </div>
              </div>
            </div>
            
            <div style={{ backgroundColor: '#e6f7ff', padding: '8px', borderRadius: '4px', marginBottom: '8px' }}>
              <Typography.Text strong style={{ fontSize: '12px', color: '#1890ff' }}>實例說明：</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                假設某房型 ADR = 4,000元，住房率 = 75%，則 RevPAR = 4,000 × 0.75 = 3,000元
                <br />這表示該房型每間可售房間平均貢獻 3,000元收入
              </Typography.Text>
            </div>
          </div>
        </div>
        <Form>
          <Row gutter={16} justify="space-between" align="bottom">
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="房型篩選">
                <PmsRoomSelect
                  mode="multiple"
                  placeholder="選擇房型（預設全選）"
                  value={this.state.selectedRoomTypes}
                  onChange={this.handleRoomTypeChange}
                  style={{ width: '100%' }}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item label="期間篩選">
                {this.state.periodType === 'day' && (
                  <DatePicker.RangePicker
                    value={this.state.dateRange}
                    onChange={this.handleDateRangeChange}
                    style={{ width: '100%' }}
                    placeholder={['開始日期', '結束日期']}
                    allowClear
                  />
                )}
                {this.state.periodType === 'month' && (
                  <DatePicker.RangePicker
                    value={this.state.monthRange}
                    onChange={this.handleMonthRangeChange}
                    picker="month"
                    style={{ width: '100%' }}
                    placeholder={['開始月份', '結束月份']}
                    allowClear
                  />
                )}
                {this.state.periodType === 'year' && (
                  <DatePicker.RangePicker
                    value={this.state.yearRange}
                    onChange={this.handleYearRangeChange}
                    picker="year"
                    style={{ width: '100%' }}
                    placeholder={['開始年份', '結束年份']}
                    allowClear
                  />
                )}
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item label="週期">
                <Select
                  value={this.state.periodType}
                  onChange={this.handleSelectPeriodType}
                >
                  <Select.Option key="year">年</Select.Option>
                  <Select.Option key="month">月</Select.Option>
                  <Select.Option key="day">日</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} lg={6}>
              <Form.Item>
                <CSVDownloadButton
                  columns={this.getColumns()}
                  data={this.getTableData()}
                  filename="ADR_RevPAR_比較"
                  overrideRender={{
                    date: (value) => value, // 保持日期原樣
                    // 為所有 ADR 和 RevPAR 欄位添加 overrideRender
                    ...(() => {
                      const { revparData, selectedRoomTypes } = this.state;
                      const filteredRevparData = selectedRoomTypes.length === 0
                        ? revparData
                        : revparData.filter(roomData =>
                            roomData.roomType === 'ALL' || selectedRoomTypes.includes(roomData.roomType)
                          );
                      
                      const overrides = {};
                      filteredRevparData.forEach(roomData => {
                        // ADR 欄位
                        overrides[`adr_${roomData.roomType}`] = (value) => {
                          return value !== null && value !== undefined ? value.toString() : '';
                        };
                        // RevPAR 欄位
                        overrides[`revpar_${roomData.roomType}`] = (value) => {
                          return value !== null && value !== undefined ? value.toString() : '';
                        };
                      });
                      return overrides;
                    })()
                  }}
                >
                  <DownloadOutlined /> 匯出 CSV
                </CSVDownloadButton>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          columns={columns}
          dataSource={tableData}
          scroll={{ x: "max-content" }}
          loading={this.state.loading}
          size="small"
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
          }}
          bordered
          summary={(pageData) => {
            const { revparData, selectedRoomTypes } = this.state;
            if (!pageData.length || !revparData.length) return null;

            // Filter room types based on selection (same logic as in getColumns)
            const filteredRevparData = selectedRoomTypes.length === 0
              ? revparData // 空陣列表示全選
              : revparData.filter(roomData =>
                  roomData.roomType === 'ALL' || selectedRoomTypes.includes(roomData.roomType)
                );

            // Sort room types by average RevPAR (same logic as in getColumns)
            const sortedRevparData = [...filteredRevparData].sort((a, b) => {
              // 'ALL' 永遠放在最前面
              if (a.roomType === 'ALL') return -1;
              if (b.roomType === 'ALL') return 1;
              
              // 其他房型按平均 RevPAR 排序，只計算當前頁面顯示的數據
              const currentPageDates = pageData.map(row => row.date);
              const filteredDataA = a.data.filter(item => currentPageDates.includes(item.dat));
              const filteredDataB = b.data.filter(item => currentPageDates.includes(item.dat));
              
              const avgA = filteredDataA.length > 0 ?
                filteredDataA.reduce((sum, item) => sum + (item.revpar || 0), 0) / filteredDataA.length : 0;
              const avgB = filteredDataB.length > 0 ?
                filteredDataB.reduce((sum, item) => sum + (item.revpar || 0), 0) / filteredDataB.length : 0;
              return avgB - avgA; // 降序排列，RevPAR 高的在前
            });

            return (
              <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                <Table.Summary.Cell index={0} align="center">
                  <strong>平均值</strong>
                </Table.Summary.Cell>
                {sortedRevparData.map((roomData, index) => {
                  // Calculate average ADR for this room type from current page data
                  const validAdrValues = pageData
                    .map(record => record[`adr_${roomData.roomType}`])
                    .filter(val => val !== null && val !== undefined && val > 0);
                  
                  const adrAverage = validAdrValues.length > 0
                    ? validAdrValues.reduce((sum, val) => sum + Number(val), 0) / validAdrValues.length
                    : 0;

                  // Calculate average RevPAR for this room type from current page data
                  const validRevparValues = pageData
                    .map(record => record[`revpar_${roomData.roomType}`])
                    .filter(val => val !== null && val !== undefined && val > 0);
                  
                  const revparAverage = validRevparValues.length > 0
                    ? validRevparValues.reduce((sum, val) => sum + Number(val), 0) / validRevparValues.length
                    : 0;

                  let revparColor = '';
                  if (revparAverage < 1000) {
                    revparColor = '#cf1322'; // 紅色 - 表現較差
                  } else if (revparAverage >= 1000 && revparAverage <= 2500) {
                    revparColor = '#faad14'; // 黃色 - 表現中等
                  } else if (revparAverage > 2500) {
                    revparColor = '#3f8600'; // 綠色 - 表現優秀
                  }

                  return [
                    // ADR Summary Cell
                    <Table.Summary.Cell key={`adr_${roomData.roomType}`} index={index * 2 + 1} align="right">
                      <strong style={{ color: '#1890ff' }}>
                        {adrAverage > 0 ? Math.round(adrAverage).toLocaleString() : '-'}
                      </strong>
                    </Table.Summary.Cell>,
                    // RevPAR Summary Cell
                    <Table.Summary.Cell key={`revpar_${roomData.roomType}`} index={index * 2 + 2} align="right">
                      <strong style={{ color: revparColor }}>
                        {revparAverage > 0 ? Math.round(revparAverage).toLocaleString() : '-'}
                      </strong>
                    </Table.Summary.Cell>
                  ];
                }).flat()}
              </Table.Summary.Row>
            );
          }}
        />
      </Spin>
    );
  }
}

export default RevParComparisonPage;